import React, { useState, useEffect, useCallback } from 'react';
import AdminLayout from '../../components/layout/AdminLayout';
import CheatSheet from '../../components/admin/CheatSheet';
import { Bell, Send, Loader2, Clock, Users, ChevronRight } from 'lucide-react';
import { apiClient } from '../../api/client';

const AdminNotifications = () => {
  const [history, setHistory] = useState([]);
  const [scheduled, setScheduled] = useState([]);
  const [loading, setLoading] = useState(true);
  const [target, setTarget] = useState('all');
  const [title, setTitle] = useState('');
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);
  const [tab, setTab] = useState('compose');

  const load = useCallback(async () => {
    try {
      const [h, s] = await Promise.all([
        apiClient.request('/api/admin/notifications/history'),
        apiClient.request('/api/admin/notifications/scheduled').catch(() => ({ notifications: [] })),
      ]);
      setHistory(h.batches || []);
      setScheduled(s.notifications || []);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const sendNotification = async () => {
    if (!title || !message) return;
    setSending(true);
    try {
      const r = await apiClient.request('/api/admin/notifications/send', {
        method: 'POST', body: JSON.stringify({ target, title, message }),
      });
      setResult(r);
      setTitle(''); setMessage('');
      load();
    } catch (e) { console.error(e); }
    setSending(false);
  };

  if (loading) return <AdminLayout pageTitle="Notifications">
      <CheatSheet pageId="notifications" /><div className="flex items-center justify-center min-h-[60vh]"><Loader2 className="w-8 h-8 animate-spin" style={{ color: '#F5831F' }} /></div></AdminLayout>;

  return (
    <AdminLayout pageTitle="Notifications">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
        {/* Compose */}
        <div className="bg-white p-5 rounded-lg" style={{ border: '1px solid #E0E0E0' }}>
          <h3 className="text-sm font-bold mb-4 flex items-center gap-2" style={{ color: '#1A1209' }}><Send size={16} /> Compose Notification</h3>
          <div className="space-y-3">
            <div>
              <label className="text-xs font-medium block mb-1" style={{ color: '#44403A' }}>Audience</label>
              <select value={target} onChange={e => setTarget(e.target.value)} className="w-full px-3 py-2.5 border rounded-lg text-sm" style={{ borderColor: '#E0E0E0' }}>
                <option value="all">All Users</option>
                <option value="segment:b2c_teen">B2C Teens</option>
                <option value="segment:b2c_adult">B2C Adults</option>
                <option value="segment:b2b_school">B2B Schools</option>
              </select>
            </div>
            <div>
              <label className="text-xs font-medium block mb-1" style={{ color: '#44403A' }}>Title</label>
              <input value={title} onChange={e => setTitle(e.target.value)} placeholder="Notification title..." className="w-full px-3 py-2.5 border rounded-lg text-sm" style={{ borderColor: '#E0E0E0' }} />
            </div>
            <div>
              <label className="text-xs font-medium block mb-1" style={{ color: '#44403A' }}>Message <span className="text-gray-400">({message.length}/300)</span></label>
              <textarea value={message} onChange={e => setMessage(e.target.value.slice(0, 300))} placeholder="Write your message..." className="w-full px-3 py-2.5 border rounded-lg text-sm h-28 resize-none" style={{ borderColor: '#E0E0E0' }} />
            </div>
            <button onClick={sendNotification} disabled={sending || !title || !message}
              className="w-full py-2.5 rounded-lg font-medium text-sm text-white disabled:opacity-50 flex items-center justify-center gap-2"
              style={{ backgroundColor: '#F5831F' }}>
              {sending ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />} Send Now
            </button>
            {result && <p className="text-xs text-green-600 mt-1">Sent to {result.recipients} users</p>}
          </div>
        </div>

        {/* History */}
        <div>
          <div className="flex gap-1 bg-gray-100 rounded-lg p-1 mb-4">
            {['history', 'scheduled'].map(t => (
              <button key={t} onClick={() => setTab(t)}
                className={`flex-1 py-2 px-3 rounded-md text-sm font-medium capitalize transition-colors ${tab === t ? 'bg-white shadow-sm' : 'text-gray-500'}`}
                style={tab === t ? { color: '#1A1209' } : {}}>{t}</button>
            ))}
          </div>

          {tab === 'history' && (
            <div className="bg-white rounded-lg" style={{ border: '1px solid #E0E0E0' }}>
              <div className="p-4 border-b" style={{ borderColor: '#E0E0E0' }}>
                <h3 className="text-sm font-bold" style={{ color: '#1A1209' }}>Sent Notifications</h3>
              </div>
              <div className="divide-y" style={{ borderColor: '#E0E0E0' }}>
                {history.length === 0 ? (
                  <p className="p-6 text-center text-sm" style={{ color: '#44403A' }}>No notifications sent yet</p>
                ) : history.map((b, i) => (
                  <div key={i} className="p-4">
                    <div className="flex justify-between mb-1">
                      <span className="text-sm font-medium" style={{ color: '#333333' }}>{b.title}</span>
                      <span className="text-xs" style={{ color: '#6B6257' }}>{b.created_at?.slice(0, 10)}</span>
                    </div>
                    <p className="text-xs mb-2" style={{ color: '#44403A' }}>{b.message?.slice(0, 100)}</p>
                    <div className="flex gap-4 text-xs" style={{ color: '#44403A' }}>
                      <span>Sent: {b.total}</span>
                      <span>Read: {b.read_count}</span>
                      <span>Confirmed: {b.confirmed_count}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {tab === 'scheduled' && (
            <div className="bg-white rounded-lg" style={{ border: '1px solid #E0E0E0' }}>
              <div className="p-4 border-b" style={{ borderColor: '#E0E0E0' }}>
                <h3 className="text-sm font-bold flex items-center gap-2" style={{ color: '#1A1209' }}><Clock size={14} /> Scheduled</h3>
              </div>
              <div className="divide-y" style={{ borderColor: '#E0E0E0' }}>
                {scheduled.length === 0 ? (
                  <p className="p-6 text-center text-sm" style={{ color: '#44403A' }}>No scheduled notifications</p>
                ) : scheduled.map((s, i) => (
                  <div key={i} className="p-4">
                    <div className="flex justify-between"><span className="text-sm font-medium">{s.title}</span><span className="text-xs px-2 py-0.5 rounded-full bg-amber-100 text-amber-700">{s.status}</span></div>
                    <p className="text-xs mt-1" style={{ color: '#44403A' }}>Scheduled: {s.scheduled_for}</p>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </AdminLayout>
  );
};

export default AdminNotifications;
